import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Skeleton,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import {
  MdAdd,
  MdArrowDropDown,
  MdCallSplit,
  MdCheck,
  MdClose,
  MdDownload,
  MdFilePresent,
  MdGridView,
  MdLock,
  MdPictureAsPdf,
  MdRemove,
  MdUploadFile,
} from 'react-icons/md';
import { readFileAsArrayBuffer } from '../utils/pdfExport';
import {
  buildPageGroupMap,
  everyNGroups,
  everyPageGroups,
  parseRanges,
} from '../utils/rangeParser';
import { previewFileNames, splitAndExport } from '../utils/splitExport';

const MotionBox = motion(Box);

// ── Constants ─────────────────────────────────────────────────────────────────

const ZOOM_PRESETS = [25, 50, 75, 100, 125, 150, 200];
const BASE_CANVAS_W = 148;

/** Soft color palette for group-coded page tiles. */
const GROUP_PALETTE = [
  { strip: '#63B3ED', bg: '#EBF8FF', border: '#90CDF4', text: '#2B6CB0' },
  { strip: '#F6AD55', bg: '#FFFAF0', border: '#FBD38D', text: '#C05621' },
  { strip: '#68D391', bg: '#F0FFF4', border: '#9AE6B4', text: '#276749' },
  { strip: '#B794F4', bg: '#FAF5FF', border: '#D6BCFA', text: '#6B46C1' },
  { strip: '#F687B3', bg: '#FFF5F7', border: '#FEB2C8', text: '#97266D' },
  { strip: '#4FD1C5', bg: '#E6FFFA', border: '#81E6D9', text: '#285E61' },
  { strip: '#F6E05E', bg: '#FFFFF0', border: '#FAF089', text: '#744210' },
  { strip: '#FC8181', bg: '#FFF5F5', border: '#FEB2B2', text: '#9B2C2C' },
];

const SPLIT_MODES = [
  {
    id: 'every-page',
    label: 'Every page',
    desc: 'One PDF per page',
  },
  {
    id: 'every-n',
    label: 'Every N pages',
    desc: 'Equal groups of N pages',
  },
  {
    id: 'ranges',
    label: 'Page ranges',
    desc: 'Custom range like 1-3, 5',
  },
  {
    id: 'extract',
    label: 'Extract pages',
    desc: 'Pick specific pages',
  },
];

const uid = () => Math.random().toString(36).slice(2, 9);

// ── SplitPageTile ─────────────────────────────────────────────────────────────
// A non-sortable page thumbnail with optional selection circle or group badge.

const SplitPageTile = ({
  page,
  pdfDoc,
  displayNumber,
  mode,
  groupIndex,
  isSelected,
  onSelect,
  canvasWidth = BASE_CANVAS_W,
}) => {
  const canvasRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [canvasH, setCanvasH] = useState(Math.round(canvasWidth * 1.414));
  const [isHovered, setIsHovered] = useState(false);

  const isExtractMode = mode === 'extract';
  const showGroupBadge = (mode === 'every-n' || mode === 'ranges') && groupIndex !== undefined;
  const showEveryPageBadge = mode === 'every-page';
  const palette = showGroupBadge ? GROUP_PALETTE[groupIndex % GROUP_PALETTE.length] : null;

  useEffect(() => {
    if (!pdfDoc) return;
    setRendered(false);
    setCanvasH(Math.round(canvasWidth * 1.414));
    let cancelled = false;

    const render = async () => {
      try {
        const pdfPage = await pdfDoc.getPage(page.originalIndex + 1);
        const naturalVP = pdfPage.getViewport({ scale: 1 });
        const viewport = pdfPage.getViewport({ scale: canvasWidth / naturalVP.width });
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        setCanvasH(viewport.height);
        await pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        if (!cancelled) setRendered(true);
      } catch { /* non-fatal */ }
    };

    render();
    return () => { cancelled = true; };
  }, [pdfDoc, page.originalIndex, canvasWidth]);

  const handleClick = useCallback(() => {
    if (isExtractMode && onSelect) onSelect(page.id);
  }, [isExtractMode, onSelect, page.id]);

  // Border and background based on state
  let borderColor = isSelected ? 'blue.400' : isHovered ? 'gray.300' : 'gray.200';
  let bg = isSelected ? 'blue.50' : 'white';

  if (showGroupBadge && !isSelected) {
    borderColor = palette.border;
    bg = palette.bg;
  }
  if (showEveryPageBadge && !isSelected) {
    borderColor = 'gray.200';
  }

  return (
    <Box
      bg={bg}
      borderWidth="2px"
      borderColor={borderColor}
      boxShadow={
        isSelected
          ? '0 0 0 3px rgba(66,153,225,0.18), 0 4px 12px rgba(0,0,0,0.10)'
          : '0 1px 4px rgba(0,0,0,0.06)'
      }
      borderRadius="14px"
      overflow="visible"
      cursor={isExtractMode ? 'pointer' : 'default'}
      position="relative"
      transition="border-color 0.15s, box-shadow 0.15s, background 0.15s"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      role={isExtractMode ? 'button' : undefined}
      aria-pressed={isExtractMode ? isSelected : undefined}
      aria-label={isExtractMode ? `Page ${displayNumber}${isSelected ? ', selected' : ''}` : undefined}
    >
      {/* ── Selection circle (extract mode only) ── */}
      {isExtractMode && (
        <Box
          position="absolute"
          top={2}
          left={2}
          zIndex={4}
          w={5}
          h={5}
          borderRadius="full"
          borderWidth="2px"
          borderColor={isSelected ? 'blue.500' : 'blue.300'}
          bg={isSelected ? 'blue.500' : 'white'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          opacity={isSelected || isHovered ? 1 : 0}
          transition="all 0.15s"
          pointerEvents={isSelected || isHovered ? 'auto' : 'none'}
          cursor="pointer"
          onClick={(e) => { e.stopPropagation(); if (onSelect) onSelect(page.id); }}
          _hover={{ borderColor: 'blue.400', transform: 'scale(1.5)' }}
        >
          {isSelected && <Icon as={MdCheck} color="white" boxSize={3} />}
        </Box>
      )}

      {/* ── Group badge (every-n / ranges modes) ── */}
      {showGroupBadge && (
        <Box
          position="absolute"
          top={1.5}
          right={1.5}
          zIndex={4}
          borderRadius="full"
          px={1.5}
          py="2px"
          bg={palette.bg}
          border={`1.5px solid ${palette.border}`}
          userSelect="none"
          pointerEvents="none"
        >
          <Text fontSize="9px" fontWeight="700" color={palette.text} lineHeight="1.2">
            {groupIndex + 1}
          </Text>
        </Box>
      )}

      {/* ── Every-page label (subtle file indicator) ── */}
      {showEveryPageBadge && (
        <Box
          position="absolute"
          top={1.5}
          right={1.5}
          zIndex={4}
          borderRadius="full"
          px={1.5}
          py="2px"
          bg="gray.50"
          border="1.5px solid"
          borderColor="gray.200"
          userSelect="none"
          pointerEvents="none"
        >
          <Text fontSize="9px" fontWeight="600" color="gray.400" lineHeight="1.2">
            {displayNumber}
          </Text>
        </Box>
      )}

      {/* ── Canvas / preview area ── */}
      <Box
        mt={3}
        bg="gray.50"
        borderTopLeftRadius="12px"
        borderTopRightRadius="12px"
        overflow="hidden"
        minH={`${canvasH}px`}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {!rendered && (
          <Skeleton
            w={`${canvasWidth}px`}
            h={`${canvasH}px`}
            startColor="gray.100"
            endColor="gray.200"
            speed={1.2}
          />
        )}
        <canvas
          ref={canvasRef}
          style={{ display: rendered ? 'block' : 'none', maxWidth: '100%' }}
        />
      </Box>

      {/* ── Page number ── */}
      <Flex justify="center" pt={1} pb="4px" px={2} mt="-2px" transform="translateY(-4px)">
        <Text
          as="span"
          fontSize="sm"
          fontWeight="600"
          lineHeight="1"
          color={isSelected ? 'blue.500' : showGroupBadge ? palette.text : 'gray.500'}
          letterSpacing="0.03em"
          transition="color 0.15s"
        >
          {displayNumber}
        </Text>
      </Flex>

      {/* ── Group color strip at bottom ── */}
      {showGroupBadge && (
        <Box
          h="3px"
          bg={palette.strip}
          borderBottomLeftRadius="12px"
          borderBottomRightRadius="12px"
          mt="-4px"
          mx="2px"
          mb="2px"
        />
      )}
    </Box>
  );
};

// ── UploadArea ────────────────────────────────────────────────────────────────

const UploadArea = ({ onFileDrop }) => {
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    noClick: true,
    onDrop: (accepted) => { if (accepted[0]) onFileDrop(accepted[0]); },
  });

  return (
    <Flex flex={1} align="center" justify="center" p={8}>
      <Box
        {...getRootProps()}
        w="full"
        maxW="520px"
        bg={isDragActive ? 'blue.50' : 'white'}
        border="2px dashed"
        borderColor={isDragActive ? 'blue.400' : 'gray.200'}
        borderRadius="20px"
        p={10}
        textAlign="center"
        transition="all 0.2s"
        cursor="default"
      >
        <input {...getInputProps()} />
        <Flex
          w={16}
          h={16}
          borderRadius="18px"
          bg={isDragActive ? 'blue.100' : 'gray.100'}
          align="center"
          justify="center"
          mx="auto"
          mb={5}
          transition="background 0.2s"
        >
          <Icon
            as={MdCallSplit}
            boxSize={8}
            color={isDragActive ? 'blue.500' : 'gray.400'}
            transition="color 0.2s"
          />
        </Flex>

        <Text fontSize="xl" fontWeight="700" color="gray.800" mb={1}>
          {isDragActive ? 'Drop your PDF here' : 'Split your PDF'}
        </Text>
        <Text fontSize="sm" color="gray.500" mb={6}>
          Drag and drop a PDF, or click to browse your files
        </Text>

        <Button
          onClick={(e) => { e.stopPropagation(); open(); }}
          colorScheme="blue"
          size="md"
          borderRadius="10px"
          fontWeight="600"
          leftIcon={<MdUploadFile size={17} />}
          px={6}
        >
          Select PDF
        </Button>

        <Flex align="center" justify="center" gap={1.5} mt={6}>
          <Icon as={MdLock} boxSize={3} color="gray.400" />
          <Text fontSize="xs" color="gray.400">
            Processed locally in your browser — your file never leaves this device
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
};

// ── ModeSelector ──────────────────────────────────────────────────────────────

const ModeSelector = ({ mode, onChange }) => (
  <Flex
    bg="gray.100"
    borderRadius="12px"
    p={1}
    gap={1}
    wrap={{ base: 'wrap', md: 'nowrap' }}
  >
    {SPLIT_MODES.map((m) => (
      <Box
        key={m.id}
        flex={1}
        minW="100px"
        px={3}
        py={2}
        borderRadius="9px"
        bg={mode === m.id ? 'white' : 'transparent'}
        boxShadow={mode === m.id ? '0 1px 4px rgba(0,0,0,0.10)' : 'none'}
        cursor="pointer"
        transition="all 0.15s"
        onClick={() => onChange(m.id)}
        textAlign="center"
        _hover={mode !== m.id ? { bg: 'gray.50' } : {}}
        userSelect="none"
      >
        <Text
          fontSize="sm"
          fontWeight={mode === m.id ? '700' : '500'}
          color={mode === m.id ? 'blue.700' : 'gray.600'}
          lineHeight="1.3"
        >
          {m.label}
        </Text>
        <Text fontSize="10px" color={mode === m.id ? 'blue.500' : 'gray.400'} lineHeight="1.3" mt={0.5}>
          {m.desc}
        </Text>
      </Box>
    ))}
  </Flex>
);

// ── ModeControls ──────────────────────────────────────────────────────────────

const ModeControls = ({
  mode,
  totalPages,
  nValue,
  setNValue,
  rangeInput,
  setRangeInput,
  rangeError,
  selectedCount,
  onDeselectAll,
}) => {
  if (mode === 'every-page') {
    return (
      <Flex
        align="center"
        gap={2}
        px={4}
        py={3}
        bg="blue.50"
        borderRadius="12px"
        borderWidth="1px"
        borderColor="blue.100"
      >
        <Icon as={MdGridView} color="blue.400" boxSize={4} flexShrink={0} />
        <Text fontSize="sm" color="blue.700">
          Each page will be exported as a separate PDF.
          {totalPages > 1 && (
            <Text as="span" fontWeight="600"> {totalPages} files</Text>
          )}
          {totalPages > 1 && ' will be packaged in a ZIP archive.'}
        </Text>
      </Flex>
    );
  }

  if (mode === 'every-n') {
    const groupCount = nValue >= 1 ? Math.ceil(totalPages / nValue) : 0;
    return (
      <Flex align="center" gap={3} flexWrap="wrap">
        <Text fontSize="sm" fontWeight="500" color="gray.700" flexShrink={0}>
          Split every
        </Text>
        <NumberInput
          min={1}
          max={totalPages}
          value={nValue}
          onChange={(_, val) => setNValue(isNaN(val) ? 1 : val)}
          w="90px"
          size="sm"
        >
          <NumberInputField
            textAlign="center"
            borderRadius="8px"
            fontWeight="600"
            borderColor="gray.200"
            _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #63B3ED' }}
          />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text fontSize="sm" color="gray.600" flexShrink={0}>
          pages
        </Text>
        {groupCount > 0 && (
          <Text fontSize="sm" color="gray.400">
            → <Text as="span" fontWeight="600" color="gray.600">{groupCount} group{groupCount !== 1 ? 's' : ''}</Text>
            {groupCount > 1 && ', packaged as a ZIP'}
          </Text>
        )}
      </Flex>
    );
  }

  if (mode === 'ranges') {
    return (
      <VStack align="stretch" spacing={1.5}>
        <Flex align="baseline" gap={2}>
          <Text fontSize="sm" fontWeight="500" color="gray.700">
            Page ranges
          </Text>
          <Text fontSize="xs" color="gray.400">
            Comma-separated — e.g. 1-3, 5, 7-10
          </Text>
        </Flex>
        <Input
          value={rangeInput}
          onChange={(e) => setRangeInput(e.target.value)}
          placeholder="1-3, 5, 7-10"
          fontFamily="mono"
          size="sm"
          borderRadius="8px"
          borderColor={rangeError ? 'red.300' : 'gray.200'}
          _focus={{ borderColor: rangeError ? 'red.400' : 'blue.400', boxShadow: rangeError ? '0 0 0 1px #FC8181' : '0 0 0 1px #63B3ED' }}
        />
        <AnimatePresence>
          {rangeError && (
            <MotionBox
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
            >
              <Alert status="error" size="sm" borderRadius="8px" py={2} px={3}>
                <AlertIcon boxSize={3.5} />
                <AlertDescription fontSize="xs">{rangeError}</AlertDescription>
              </Alert>
            </MotionBox>
          )}
        </AnimatePresence>
      </VStack>
    );
  }

  if (mode === 'extract') {
    return (
      <Flex align="center" gap={3}>
        <Flex
          align="center"
          gap={2}
          px={4}
          py={3}
          bg={selectedCount > 0 ? 'blue.50' : 'gray.50'}
          borderRadius="12px"
          borderWidth="1px"
          borderColor={selectedCount > 0 ? 'blue.100' : 'gray.100'}
          flex={1}
          transition="all 0.2s"
        >
          <Icon
            as={selectedCount > 0 ? MdCheck : MdGridView}
            color={selectedCount > 0 ? 'blue.400' : 'gray.400'}
            boxSize={4}
            flexShrink={0}
          />
          <Text fontSize="sm" color={selectedCount > 0 ? 'blue.700' : 'gray.500'}>
            {selectedCount > 0 ? (
              <>
                <Text as="span" fontWeight="700">{selectedCount}</Text>
                {' page'}{selectedCount !== 1 ? 's' : ''} selected — will be exported as one PDF
              </>
            ) : (
              'Click on page thumbnails below to select pages for extraction'
            )}
          </Text>
        </Flex>
        {selectedCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="gray"
            leftIcon={<MdClose size={14} />}
            onClick={onDeselectAll}
            borderRadius="8px"
            fontWeight="500"
            flexShrink={0}
          >
            Clear
          </Button>
        )}
      </Flex>
    );
  }

  return null;
};

// ── ZoomControl ───────────────────────────────────────────────────────────────

const ZoomControl = ({ zoom, setZoom }) => (
  <HStack spacing={0.5}>
    <Tooltip label="Zoom out" openDelay={500} hasArrow>
      <IconButton
        icon={<MdRemove size={14} />}
        aria-label="Zoom out"
        size="xs"
        variant="ghost"
        colorScheme="gray"
        borderRadius="6px"
        onClick={() =>
          setZoom((z) => {
            const idx = ZOOM_PRESETS.indexOf(z);
            return idx > 0 ? ZOOM_PRESETS[idx - 1] : z;
          })
        }
        isDisabled={zoom <= ZOOM_PRESETS[0]}
      />
    </Tooltip>
    <Menu>
      <MenuButton
        as={Button}
        size="xs"
        variant="ghost"
        colorScheme="gray"
        rightIcon={<MdArrowDropDown size={14} />}
        borderRadius="6px"
        fontWeight="600"
        fontSize="xs"
        px={2}
        minW="58px"
      >
        {zoom}%
      </MenuButton>
      <MenuList minW="100px" shadow="lg" borderRadius="10px" py={1}>
        {ZOOM_PRESETS.map((p) => (
          <MenuItem
            key={p}
            fontSize="sm"
            fontWeight={zoom === p ? '700' : '400'}
            color={zoom === p ? 'blue.600' : 'gray.700'}
            onClick={() => setZoom(p)}
            borderRadius="6px"
            mx={1}
            px={3}
          >
            {p}%
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
    <Tooltip label="Zoom in" openDelay={500} hasArrow>
      <IconButton
        icon={<MdAdd size={14} />}
        aria-label="Zoom in"
        size="xs"
        variant="ghost"
        colorScheme="gray"
        borderRadius="6px"
        onClick={() =>
          setZoom((z) => {
            const idx = ZOOM_PRESETS.indexOf(z);
            return idx < ZOOM_PRESETS.length - 1 ? ZOOM_PRESETS[idx + 1] : z;
          })
        }
        isDisabled={zoom >= ZOOM_PRESETS[ZOOM_PRESETS.length - 1]}
      />
    </Tooltip>
  </HStack>
);

// ── SplitPdf (main) ───────────────────────────────────────────────────────────

const SplitPdf = () => {
  const toast = useToast();

  // ── File state ──
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [pages, setPages] = useState([]); // { id, originalIndex }

  // ── Split mode state ──
  const [mode, setMode] = useState('every-page');
  const [nValue, setNValue] = useState(2);
  const [rangeInput, setRangeInput] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── UI state ──
  const [zoom, setZoom] = useState(75);
  const [isExporting, setIsExporting] = useState(false);

  const canvasWidth = Math.round(BASE_CANVAS_W * (zoom / 100));

  // ── File loading ──
  const loadFile = useCallback(async (file) => {
    setIsLoadingFile(true);
    try {
      const buffer = await readFileAsArrayBuffer(file);
      const doc = await pdfjsLib.getDocument({ data: buffer.slice(0) }).promise;
      setPdfFile(file);
      setArrayBuffer(buffer);
      setPdfDoc(doc);
      setPages(
        Array.from({ length: doc.numPages }, (_, i) => ({
          id: `p-${i}-${uid()}`,
          originalIndex: i,
        }))
      );
      setSelectedIds(new Set());
      setRangeInput('');
      setNValue(2);
      setMode('every-page');
    } catch {
      toast({
        title: 'Could not open PDF',
        description: 'The file may be corrupted or password-protected.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsLoadingFile(false);
    }
  }, [toast]);

  const unload = useCallback(() => {
    if (pdfDoc) { try { pdfDoc.destroy(); } catch { /* ok */ } }
    setPdfFile(null);
    setPdfDoc(null);
    setArrayBuffer(null);
    setPages([]);
    setSelectedIds(new Set());
    setRangeInput('');
    setNValue(2);
    setMode('every-page');
  }, [pdfDoc]);

  // ── Dropzone for "change file" ──
  const { getRootProps: getDropRootProps, getInputProps: getDropInputProps } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    noClick: true,
    onDrop: (accepted) => { if (accepted[0]) loadFile(accepted[0]); },
  });

  // ── Selection (extract mode) ──
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const deselectAll = useCallback(() => setSelectedIds(new Set()), []);

  // ── Computed groups & validation ──
  const { groups, rangeError } = useMemo(() => {
    if (!pdfDoc || pages.length === 0) return { groups: [], rangeError: null };
    switch (mode) {
      case 'every-page':
        return { groups: everyPageGroups(pages.length), rangeError: null };
      case 'every-n':
        return { groups: everyNGroups(pages.length, nValue), rangeError: null };
      case 'ranges': {
        const result = parseRanges(rangeInput, pages.length);
        return { groups: result.groups, rangeError: result.error };
      }
      case 'extract':
        return { groups: [], rangeError: null };
      default:
        return { groups: [], rangeError: null };
    }
  }, [mode, pages.length, nValue, rangeInput, pdfDoc]);

  const pageGroupMap = useMemo(() => buildPageGroupMap(groups), [groups]);

  const isValid = useMemo(() => {
    if (!pdfDoc || pages.length === 0) return false;
    if (mode === 'every-page') return true;
    if (mode === 'every-n') return Number.isInteger(nValue) && nValue >= 1 && nValue <= pages.length;
    if (mode === 'ranges') return !rangeError && groups.length > 0;
    if (mode === 'extract') return selectedIds.size > 0;
    return false;
  }, [mode, pdfDoc, pages, nValue, groups, rangeError, selectedIds]);

  // ── Output preview labels ──
  const baseName = pdfFile ? pdfFile.name.replace(/\.pdf$/i, '') : '';
  const outputGroups = useMemo(
    () =>
      mode === 'extract'
        ? selectedIds.size > 0
          ? [
              Array.from(selectedIds)
                .map((id) => pages.find((p) => p.id === id)?.originalIndex ?? -1)
                .filter((i) => i >= 0)
                .sort((a, b) => a - b),
            ]
          : []
        : groups,
    [mode, selectedIds, pages, groups],
  );
  const namingMode = mode === 'every-page' ? 'page' : mode === 'every-n' ? 'part' : mode === 'ranges' ? 'range' : 'selected';
  const filePreview = pdfFile ? previewFileNames(baseName, outputGroups, namingMode) : [];
  const outputCount = outputGroups.length;
  const willZip = outputCount > 1;

  // ── Export ──
  const handleSplit = useCallback(async () => {
    if (!isValid || isExporting) return;
    setIsExporting(true);
    try {
      await splitAndExport(arrayBuffer, outputGroups, pdfFile.name, namingMode);
      toast({
        title: willZip ? 'ZIP downloaded!' : 'PDF downloaded!',
        description: willZip
          ? `${outputCount} files packaged in ${baseName}-split.zip`
          : 'Your split PDF is ready.',
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsExporting(false);
    }
  }, [isValid, isExporting, arrayBuffer, outputGroups, pdfFile, namingMode, willZip, outputCount, baseName, toast]);

  // ── Render: empty state ──
  if (!pdfFile && !isLoadingFile) {
    return <UploadArea onFileDrop={loadFile} />;
  }

  if (isLoadingFile) {
    return (
      <Flex flex={1} align="center" justify="center" direction="column" gap={4}>
        <Box
          w={14}
          h={14}
          borderRadius="16px"
          bg="blue.50"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon as={MdPictureAsPdf} boxSize={7} color="blue.400" />
        </Box>
        <VStack spacing={1}>
          <Text fontWeight="600" color="gray.700">Loading PDF…</Text>
          <Text fontSize="sm" color="gray.400">Generating page previews</Text>
        </VStack>
      </Flex>
    );
  }

  // ── Render: workspace ──
  return (
    <Flex flex={1} overflow="hidden" minH={0}>

      {/* Hidden dropzone input (triggered from right panel button) */}
      <Box {...getDropRootProps()} display="none">
        <input {...getDropInputProps()} />
      </Box>

      {/* ── Main scrollable area ── */}
      <Box flex={1} overflow="auto" p={6} minW={0}>

        {/* ── Mode selector ── */}
        <ModeSelector mode={mode} onChange={(m) => { setMode(m); deselectAll(); }} />

        {/* ── Mode-specific controls ── */}
        <Box mt={4}>
          <ModeControls
            mode={mode}
            totalPages={pages.length}
            nValue={nValue}
            setNValue={setNValue}
            rangeInput={rangeInput}
            setRangeInput={setRangeInput}
            rangeError={rangeError}
            selectedCount={selectedIds.size}
            onDeselectAll={deselectAll}
          />
        </Box>

        {/* ── Thumbnail grid ── */}
        <Box mt={5}>
          {/* Grid header row */}
          <Flex align="center" justify="space-between" mb={3}>
            <Text fontSize="xs" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
              {pages.length} page{pages.length !== 1 ? 's' : ''}
              {mode === 'every-n' && groups.length > 0 && (
                <Text as="span" fontWeight="400" color="gray.300"> · {groups.length} group{groups.length !== 1 ? 's' : ''}</Text>
              )}
              {mode === 'ranges' && groups.length > 0 && !rangeError && (
                <Text as="span" fontWeight="400" color="gray.300"> · {groups.length} range{groups.length !== 1 ? 's' : ''}</Text>
              )}
              {mode === 'extract' && selectedIds.size > 0 && (
                <Text as="span" fontWeight="400" color="blue.400"> · {selectedIds.size} selected</Text>
              )}
            </Text>
            <ZoomControl zoom={zoom} setZoom={setZoom} />
          </Flex>

          {/* Grid */}
          <Box
            display="grid"
            gridTemplateColumns={`repeat(auto-fill, minmax(${canvasWidth + 20}px, max-content))`}
            gap={3}
          >
            {pages.map((page, idx) => (
              <SplitPageTile
                key={page.id}
                page={page}
                pdfDoc={pdfDoc}
                displayNumber={idx + 1}
                mode={mode}
                groupIndex={pageGroupMap[page.originalIndex]}
                isSelected={selectedIds.has(page.id)}
                onSelect={toggleSelect}
                canvasWidth={canvasWidth}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Right panel ── */}
      <Box
        w="240px"
        minW="240px"
        borderLeftWidth="1px"
        borderColor="gray.100"
        bg="white"
        overflow="auto"
        p={5}
        display={{ base: 'none', xl: 'flex' }}
        flexDirection="column"
        gap={5}
        flexShrink={0}
      >
        {/* ── File info ── */}
        <Flex align="center" gap={3} pb={4} borderBottomWidth="1px" borderColor="gray.100">
          <Flex
            w={9}
            h={9}
            borderRadius="10px"
            bg="blue.50"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon as={MdFilePresent} color="blue.500" boxSize={5} />
          </Flex>
          <Box flex={1} minW={0}>
            <Text fontWeight="600" color="gray.800" fontSize="sm" isTruncated>
              {pdfFile.name}
            </Text>
            <Text fontSize="xs" color="gray.400" mt={0.5}>
              {pages.length} page{pages.length !== 1 ? 's' : ''}
            </Text>
          </Box>
        </Flex>

        {/* Output summary */}
        <VStack align="stretch" spacing={3}>
          <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.1em">
            Output
          </Text>

          {/* Count summary */}
          <Box bg="gray.50" borderRadius="12px" p={4}>
            {mode === 'extract' ? (
              <>
                <Text fontSize="2xl" fontWeight="800" color={selectedIds.size > 0 ? 'blue.600' : 'gray.300'} lineHeight="1">
                  {selectedIds.size > 0 ? '1' : '—'}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {selectedIds.size > 0 ? `PDF · ${selectedIds.size} page${selectedIds.size !== 1 ? 's' : ''} selected` : 'Select pages to extract'}
                </Text>
              </>
            ) : groups.length > 0 ? (
              <>
                <Text fontSize="2xl" fontWeight="800" color="blue.600" lineHeight="1">
                  {outputCount}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {outputCount === 1 ? 'PDF file' : `PDF files · ${willZip ? 'ZIP archive' : 'single file'}`}
                </Text>
              </>
            ) : (
              <>
                <Text fontSize="2xl" fontWeight="800" color="gray.300" lineHeight="1">—</Text>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {mode === 'ranges' ? 'Enter page ranges above' : 'Configure split options'}
                </Text>
              </>
            )}
          </Box>

          {/* File name preview list */}
          {filePreview.length > 0 && (
            <VStack align="stretch" spacing={1}>
              {filePreview.map((name, i) => {
                const isMore = name.startsWith('+');
                return (
                  <Flex
                    key={i}
                    align="center"
                    gap={2}
                    px={2}
                    py={1.5}
                    borderRadius="7px"
                    bg={isMore ? 'transparent' : 'gray.50'}
                  >
                    {!isMore && (
                      <Icon
                        as={MdPictureAsPdf}
                        color="gray.300"
                        boxSize={3.5}
                        flexShrink={0}
                      />
                    )}
                    <Text
                      fontSize="10px"
                      color={isMore ? 'gray.400' : 'gray.600'}
                      fontFamily={isMore ? 'inherit' : 'mono'}
                      isTruncated={!isMore}
                      fontStyle={isMore ? 'italic' : 'normal'}
                    >
                      {name}
                    </Text>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </VStack>

        <Divider borderColor="gray.100" />

        {/* CTA */}
        <VStack align="stretch" spacing={2}>
          <Button
            colorScheme="blue"
            size="md"
            borderRadius="10px"
            fontWeight="700"
            leftIcon={<MdDownload size={17} />}
            isDisabled={!isValid}
            isLoading={isExporting}
            loadingText="Exporting…"
            onClick={handleSplit}
            boxShadow={isValid ? '0 2px 8px rgba(66,153,225,0.35)' : 'none'}
            transition="box-shadow 0.2s"
          >
            {willZip ? 'Split & Download ZIP' : 'Split & Download'}
          </Button>

          <Button
            size="sm"
            variant="ghost"
            colorScheme="gray"
            borderRadius="8px"
            fontWeight="500"
            onClick={unload}
          >
            Change file
          </Button>
        </VStack>

        {/* Validation hint */}
        <AnimatePresence>
          {!isValid && pdfDoc && (
            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Text fontSize="xs" color="gray.400" textAlign="center" lineHeight="tall">
                {mode === 'every-n' && 'Enter a valid group size (1 – ' + pages.length + ')'}
                {mode === 'ranges' && (rangeError || 'Enter valid page ranges')}
                {mode === 'extract' && 'Select at least one page'}
              </Text>
            </MotionBox>
          )}
        </AnimatePresence>

      </Box>

    </Flex>
  );
};

export default SplitPdf;
